import { Alert, Breakpoint, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import { SubjectOwnership } from '../../../common/subjectTypes'
import {
  nestedStringSorter,
  numberSorter,
  stringSorter,
  utcDescDateSorter,
} from '../../utils/generalTableHelpers'
import { sparsePagination } from '../../utils/layoutConst'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { useColumnOptionFilter } from '../../utils/useColumnOptionFilter'
import { useFetchSubjectOwnership } from '../../utils/useFetchSubjectOwnership'
import { useTableLocale } from '../../utils/useTableLocale'
import { useUniqueValues } from '../../utils/useUniqueValues'
import { Day } from '../DateFormat/DateFormat'
import { LvDetailDrawerLink } from '../LvDetailDrawerLink/LvDetailDrawerLink'
import { Money } from '../Money/Money'
import { TableSummaryExport } from '../TableSummaryExport/TableSummaryExport'

export interface SubjectDetailOwnershipTableProps {
  subjectId?: string
  searchHistory?: boolean
}

const calculateSharePercentage = (record: SubjectOwnership): number => {
  if (record.podilCitatel && record.podilJmenovatel) {
    return (record.podilCitatel / (record.podilJmenovatel || 1)) * 100
  }
  return 0
}

const cadastralAreaFilter = (value: string, record: SubjectOwnership) =>
  !!record?.katastralniUzemi?.nazev
    ?.toLowerCase()
    .includes((value as string).toLowerCase())

const rowKey = (record: SubjectOwnership) =>
  record.lv + (record?.platiOd || 'x') + record.katastralniUzemi.kod

const rowClassName = (record: SubjectOwnership) =>
  record.platiDo ? 'text-gray-400 animate-fadeIn' : 'animate-fadeIn'

const tableId = 'subjectDetail-table'
const scrollX = { x: 1000 }

export const SubjectDetailOwnershipTable: FunctionComponent<SubjectDetailOwnershipTableProps> =
  props => {
    const { fetching, data, error } = useFetchSubjectOwnership(
      props.subjectId,
      props.searchHistory
    )
    const { searchFilter, dateFilter, customSearchFilter } =
      useColumnFilters<SubjectOwnership>()

    const locale = useTableLocale(fetching)
    const typravNazevOptions = useUniqueValues(data || [], 'pravniVztah')
    const { getColumnOptionFilter } =
      useColumnOptionFilter<SubjectOwnership>(typravNazevOptions)

    const columns = useMemo<ColumnsType<SubjectOwnership>>(
      () => [
        {
          title: 'ID',
          dataIndex: 'id',
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Platné od',
          dataIndex: 'platiOd',
          className: 'w-32',
          ...dateFilter('platiOd'),
          sorter: utcDescDateSorter('platiOd'),
          render: platiOd => platiOd && <Day>{platiOd}</Day>,
        },
        ...(props.searchHistory
          ? [
              {
                title: 'Platné do',
                dataIndex: 'platiDo',
                className: 'w-32',
                ...dateFilter('platiDo'),
                sorter: utcDescDateSorter('platiDo'),
                render: (platiDo?: string) => platiDo && <Day>{platiDo}</Day>,
              },
            ]
          : []),
        {
          title: 'Typ oprávnění',
          dataIndex: 'pravniVztah',
          className: 'w-40',
          ...getColumnOptionFilter('pravniVztah'),
          sorter: stringSorter('pravniVztah'),
        },
        {
          title: 'LV',
          dataIndex: 'lv',
          className: '!text-right',
          sorter: numberSorter('lv'),
          ...searchFilter('lv', 'Vyhledat LV'),
          render: (lv, record) => (
            <LvDetailDrawerLink
              lvCode={record.katastralniUzemi?.kod}
              lvNumber={lv}
            />
          ),
        },
        {
          title: 'Katastrální území',
          dataIndex: ['katastralniUzemi', 'nazev'],
          ...customSearchFilter(
            cadastralAreaFilter,
            'Vyhledat katastrální území'
          ),
          sorter: nestedStringSorter('katastralniUzemi', 'nazev'),
          render: (_, record) => (
            <LvDetailDrawerLink
              lvCode={record.katastralniUzemi?.kod}
              lvNumber={record.lv}
              text={record.katastralniUzemi?.nazev || '-- nezjištěno --'}
            />
          ),
        },
        {
          title: 'Podíl',
          dataIndex: 'podilCitatel',
          className: '!text-right',
          render: (_, record) => {
            const percentage = calculateSharePercentage(record)

            if (record.podilCitatel && record.podilJmenovatel)
              return (
                <div>
                  <span
                    className="text-gray-400 "
                    title="Vlastnický podíl uveden v ČÚZK"
                  >
                    {record.podilCitatel} / {record.podilJmenovatel}
                  </span>
                  <span className="pl-2 inline-block w-20">
                    <Money amount={percentage} symbol="%" />
                  </span>
                </div>
              )
            return
          },
          sorter: {
            compare: (a, b) =>
              calculateSharePercentage(a) - calculateSharePercentage(b),
          },
        },
        {
          title: 'Akce',
          dataIndex: 'action',
          className: '!text-right',
          render: (_, record) => (
            <LvDetailDrawerLink
              lvCode={record.katastralniUzemi?.kod}
              lvNumber={record.lv}
              text="Zobrazit náhled"
            />
          ),
        },
      ],
      [
        customSearchFilter,
        dateFilter,
        getColumnOptionFilter,
        props.searchHistory,
        searchFilter,
      ]
    )

    const summary = useCallback(
      () => (
        <TableSummaryExport
          total={data?.length || 0}
          colSpan={(props.searchHistory ? 7 : 6) - 1}
          url="subject/subjectXlsx"
          fileName="detail-subjektu"
          dataSource={data || []}
          icon
        />
      ),
      [data, props.searchHistory]
    )

    if (error)
      return (
        <Alert
          message="Chyba při načítání vlastnických práv"
          description="Je nám líto, ale nepodařilo se najít detailní informace k požadovanému subjektu."
          type="error"
          showIcon
        />
      )

    return (
      <Table
        id={tableId}
        rowKey={rowKey}
        size="small"
        scroll={scrollX}
        dataSource={data || []}
        columns={columns}
        loading={fetching}
        summary={summary}
        locale={locale}
        rowClassName={rowClassName}
        pagination={sparsePagination}
      />
    )
  }
